import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
  Button,
  Typography,
  Paper,
} from "@material-ui/core";
import RootRef from "@material-ui/core/RootRef";

import { Error } from "../../common/Error";
import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { attachEmail } from "../../../redux/actions";
import { SarsModel } from "../../../domain/SarsModel";
import { FileRejection, useDropzone } from 'react-dropzone';

const useStyles = makeStyles((theme) => ({
  actions: {
    padding: 15,
  },
  dropzonePaper: {
    padding: 30,
    borderStyle: "dashed",
    borderWidth: 2,
    borderColor: theme.palette.grey[400],
    backgroundColor: "#fff",
    borderRadius: "3px",
    cursor: "pointer"
  },
  dropzonePaperActive: {
    padding: 30,
    borderStyle: "dashed",
    borderWidth: 2,
    borderColor: theme.palette.success.main,
    backgroundColor: "#fff",
    borderRadius: "3px",
    cursor: "pointer"
  },
  dropzoneFile: {
    fontStyle: "italic",
  },
}));

export const AttachEmailDialog: React.FC<{ 
  onClose: () => void;
  resource: SarsModel
}> = ({ onClose, resource }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [loadedFile, setLoadedFile] = useState<File | null>(null);
  const [fileRejections, setFileRejections] = useState<{ reason: string; fileName: string}[]>([]);

  const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    if (!!acceptedFiles && acceptedFiles.length === 1)
      setLoadedFile(acceptedFiles[0]);
    if (!!fileRejections && fileRejections.length > 0)
      setFileRejections(fileRejections.map(x => ({ fileName: x.file.name, reason: x.errors.map(e => e.message).join(', ') })));
  }, []);

  const handleSubmit = (id: string, file: File) => {
    dispatch(attachEmail(id, file));
    onClose();
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
    onDrop, 
    accept: [
      "application/vnd.ms-outlook",
      "application/octet-stream",
      "message/rfc822",
    ],
  });
  const { ref, ...rootProps } = getRootProps();

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle data-test="activity-title">Attach an email</DialogTitle>
      <Divider />
      <DialogContent>
        <RootRef rootRef={ref}>
          <Paper {...rootProps} className={isDragActive ? classes.dropzonePaperActive : classes.dropzonePaper}>
            <input {...getInputProps()} data-test="email-dropzone" aria-labelledby="dropzone-label" />
            <Typography id="dropzone-label" variant="body1" component="label">Drag and drop an email here, or click to select files</Typography>
            {!!loadedFile && (
              <>
                <Divider />
                <Typography variant="body2" style={{ fontStyle: "italic" }}>Loaded: {loadedFile.name}</Typography>
              </>
            )}
          </Paper>
        </RootRef>
        {!!fileRejections && (
          fileRejections.map(r => (
            <Error data-test="dropzone-error">{r.fileName} could not be processed: File must be an Outlook message (*.msg or *.eml)</Error>
          ))
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          color="primary"
          data-test="close-add-note-dialog"
        >
          Close
        </Button>
        <Button
          onClick={() => handleSubmit(resource.id, loadedFile!)}
          color="primary"
          data-test="add-note-message-button"
          disabled={!loadedFile}
        >
          Attach
        </Button>
      </DialogActions>
    </Dialog>
  );
}